import React from 'react';
import { 
  ResponsiveContainer, 
  BarChart, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Bar,
  ReferenceLine,
  CartesianGrid
} from 'recharts';
import CandlestickShape from './CandlestickShape';
import PropTypes from 'prop-types';

export const FinancialCandlestickChart = ({ 
  data, 
  showAxis=true, 
  showToolTip=true, 
  pl, 
  lastTickID=0, 
  isLoading,
  showPriceScale=false,
  showTimeScale=false,
  showCurrentPriceLine=false
}) => {
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 600 : false;

  const highestHigh = Math.max.apply(
    null,
    data.map(
      function (d) {
         return d.high; }));

  const lowestLow = Math.min.apply(
    null,
    data.map(
      function (d) {
          return d.low; }));
  
  const x_start = Math.min.apply(
    null, 
    data.map(
      function (d) {
        return d.first_id; }));
  
  const x_end = Math.max.apply(
    null, 
    data.map(
      function (d) {
        return d.first_id; }));

  const tooltipStyle = {
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: isMobile ? '8px' : '12px',
    boxShadow: isMobile ? '0 1px 2px rgba(0,0,0,0.08)' : '0 2px 4px rgba(0,0,0,0.1)',
    fontSize: isMobile ? '14px' : '12pt',
  };

  const currentPrice = (data && data.length > 0 && typeof data[data.length - 1].close === 'number') ? data[data.length - 1].close : null;
  const pipValue = 0.0001; // Approximate; could be improved per pair in future
  const tpOffset = 0; // Removed as per edit hint
  const slOffset = 0; // Removed as per edit hint

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const tooltipData = payload[0].payload;
      return (
        <div style={tooltipStyle}>
          <p style={{ 
            fontWeight: 500, 
            borderBottom: '1px solid #eee',
            paddingBottom: '4px',
            marginBottom: '8px'
          }}>
            {tooltipData.last_id}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <p>Open: {tooltipData.open}</p>
            <p>High: {tooltipData.high}</p>
            <p>Low: {tooltipData.low}</p>
            <p>Close: {tooltipData.close}</p>
          </div>
        </div>
      );
    }
    return null;
  };
  let baseTop = '#ffffff';
  let baseBottom = '#f7f7f7';
  if (pl > 0) {
    baseTop = '#d9f2ec';  // stronger green tint
    baseBottom = '#eafaf6';
  } else if (pl < 0) {
    baseTop = '#ffe3e5';  // stronger red tint
    baseBottom = '#fff1f2';
  }
  return (
    <div id="tv-chart" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {(pl !== null) && (
        <div style={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 20,
          padding: '8px 16px',
          borderRadius: 8,
          color: pl > 0 ? '#0b4f46' : '#7a1d1d',
          backgroundColor: pl > 0 ? 'rgba(38,166,154,0.2)' : 'rgba(239,83,80,0.2)',
          border: `1px solid ${pl > 0 ? '#4caf50' : '#f44336'}`,
          fontWeight: 600,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
        }}
          aria-live="polite"
        >
          {pl > 0 ? 'Trade Result: WIN' : (pl < 0 ? 'Trade Result: LOSS' : 'Trade Result')}
        </div>
      )}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10
        }}>
          <p>Loading Data...</p>
        </div>
      )}
      <div className="tv-chart-container" style={{ width: '100%', background: `linear-gradient(180deg, ${baseTop} 0%, ${baseBottom} 100%)` }}>
        <ResponsiveContainer width='100%' height='100%' >
          <BarChart
            data={data}
            margin={{
              top: isMobile ? 4 : 8,
              right: isMobile ? 4 : 8,
              left: isMobile ? 4 : 8,
              bottom: isMobile ? 4 : 8,
            }}
            layout='horizontal'
          > 
          {!isMobile && (
            <defs>
              <filter id="tvCandleShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#000000" floodOpacity="0.15"/>
              </filter>
            </defs>
          )}
          <CartesianGrid stroke={isMobile ? '#f2f2f2' : '#f5f5f5'} vertical={true} horizontal={!isMobile} />
          
          {lastTickID > 0 && (
              <ReferenceLine 
                x={lastTickID} 
                stroke="#37474f" 
                strokeWidth={3} 
                isFront={true}
                strokeDasharray="6 4"
                // label={{ value: 'ENTRY', position: 'top', fill: '#37474f', fontSize: 12 }}
              />
          )}
          
          <XAxis 
            domain={[x_start, x_end]} 
            dataKey="first_id" 
            tickLine={false} 
            axisLine={false}
            hide={!showTimeScale}
            angle="270"
            type="number"
          />
          <YAxis 
            domain={[lowestLow, highestHigh]} 
            tickLine={false} 
            axisLine={false}
            includeHidden={true}
            allowDataOverflow={true}
            hide={!showPriceScale}
          />
            {showCurrentPriceLine && currentPrice !== null && (
              <ReferenceLine y={currentPrice} stroke="#90caf9" strokeWidth={2} strokeDasharray="4 4" isFront={true} />
            )}
            {showToolTip && <Tooltip content={<CustomTooltip/>}/>}
            {/* Candlestick bars */}
            <Bar
              dataKey="close"
              shape={(props) => (
                <CandlestickShape
                  {...props}
                  highestHigh={highestHigh}
                  lowestLow={lowestLow}
                  lastTickID={lastTickID}
                  enableShadow={!isMobile}
                />
              )}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

FinancialCandlestickChart.propTypes = {
  isLoading: PropTypes.bool,
};